import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { WorkOrderSectionsCheckboxes, workOrderSectionFlagsFrom } from '../../WorkOrders/components/WorkOrderSectionsCheckboxes';
import { WorkOrderComponentLinker } from '../../WorkOrders/components/WorkOrderComponentLinker';
import { unitDisplayName } from '../../../Helpers/unitUtils';
import { WorkOrderRequirementsCheckboxes } from '../../WorkOrders/components/WorkOrderRequirementsCheckboxes';
import { X, Paperclip } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';
import { IssueWithBuilding, IssuePriority } from '../../../types/issues';
import { WorkOrderPriority, WorkOrder } from '../../../types/workOrders';
import { BuildingSummary, BaseComponent } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import ComponentPickerModal from './ComponentPickerModal';
import { translateWorkOrderPriority } from '../../../types/EnumTranslations';
import { useUsersData } from '../../../Helpers/UsersContext';
import { useBuildingsData } from '../../../Helpers/BuildingSummariesContext';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { issuesApi } from '../../../api/issuesApi';
import { issueComponentCategoryText, workOrderTitleFromIssue } from '../../../Helpers/issueUtils';
import { workOrdersApi } from '../../../api/workOrdersApi';
import { UpcomingMaintenanceItem } from '../../../hooks/management/useUpcomingMaintenance';
import { maintenanceWorkOrderDescription } from './maintenanceWorkOrderDescription';
import LocationSection from '../../../Components/LocationSection';
import AssignmentPicker, { AssignmentTab } from '../../../Components/AssignmentPicker';
import GuestLinkField from '../../../Components/GuestLinkField';
import PersonalLinkBlock from '../../../Components/PersonalLinkBlock';
import { useExternalLink } from '../../../hooks/useExternalLink';
import { useLinkValidityChoice } from '../../../hooks/useLinkValidityChoice';
import { suggestLinkValidity } from '../../../utils/linkValidity';
import CreateExternalUserForm from '../../../Components/CreateExternalUserForm';
import { ExternalUser } from '../../../types/guestAccess';
import { WORK_ORDER_LABELS } from '../../WorkOrders/workOrderSections';
import { uploadWorkOrderAttachment } from '../../../Helpers/WorkOrderAttachmentUtils';
import { useWorkOrderForm, emptyWorkOrderFormValues } from '../../../hooks/workOrders/useWorkOrderForm';
import { UploadPickerModal } from '../../../Components/UploadPickerModal';

const MAX_FILE_SIZE_MB = 50;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type AssignmentType = AssignmentTab;
// 'created': the work order exists and an external invite went out; the step
// shows the guest link so the admin can hand it over if the e-mail fails.
/** `delegation-failed`: the work order exists but delegating it failed; only the delegation is retried. */
type Step = 'main' | 'create-user' | 'created' | 'delegation-failed';

interface CreateWorkOrderModalProps {
    show: boolean;
    onHide: () => void;
    issue?: IssueWithBuilding;  // Optional - if provided, converts issue to work order
    /** Issue mode: the component's name or the category, for the prefilled title. */
    issueComponentLabel?: string;
    maintenanceItem?: UpcomingMaintenanceItem;  // Optional - if provided, creates work order from maintenance
    project?: { key: string; buildingKey: string };  // Optional - if provided, creates work order linked to project
    sourceWorkOrder?: WorkOrder;  // Optional - if provided, pre-fills the form to duplicate an existing work order
    onSuccess?: () => void;
    /** Called after a new external user is created, so the parent can refresh its user list */
    onExternalUserCreated?: () => void;
}

// Map IssuePriority to WorkOrderPriority (same values, different enums)
const mapPriority = (issuePriority: IssuePriority): WorkOrderPriority => {
    switch (issuePriority) {
        case IssuePriority.HIGH:
            return WorkOrderPriority.HIGH;
        case IssuePriority.MEDIUM:
            return WorkOrderPriority.MEDIUM;
        case IssuePriority.LOW:
            return WorkOrderPriority.LOW;
        default:
            return WorkOrderPriority.MEDIUM;
    }
};

/**
 * Modal for creating a work order
 * - If `issue` is provided: converts the issue to a work order
 * - If `maintenanceItem` is provided: creates work order from maintenance activity
 * - If `project` is provided: creates work order linked to project
 * - If `sourceWorkOrder` is provided: duplicates an existing work order (content
 *   fields only — assignment, attachments, completion data and audit are reset)
 * - If none provided: creates a standalone work order
 */
const CreateWorkOrderModal: React.FC<CreateWorkOrderModalProps> = ({
    show,
    onHide,
    issue,
    issueComponentLabel,
    maintenanceItem,
    project,
    sourceWorkOrder,
    onSuccess,
    onExternalUserCreated
}) => {
    const { users } = useUsersData();
    const { buildings } = useBuildingsData();
    const { getTemplateById, getTranslation } = useBuildComponentTypes();

    // Mode: converting issue vs maintenance vs project vs duplicate vs standalone creation
    const isIssueConversion = !!issue;
    const isMaintenanceConversion = !!maintenanceItem;
    const isDuplicate = !!sourceWorkOrder;
    // prefillsBuilding: building is determined by context, hide the dropdown
    const prefillsBuilding = isIssueConversion || isMaintenanceConversion || !!project;

    const {
        values,
        setValues,
        setField,
        error,
        setError,
        fullBuilding,
        loadingFullBuilding,
        showComponentPicker,
        setShowComponentPicker,
        componentDisplayName,
        patchComponent,
        removeComponentKey,
        validateShared,
        buildSharedFields,
        unitOptions,
    } = useWorkOrderForm();

    // Step state for create user flow
    const [step, setStep] = useState<Step>('main');

    // Assignment is owned by this shell (internal/external tabs + create-user flow).
    const [assignedTo, setAssignedTo] = useState('');
    const [assignmentType, setAssignmentType] = useState<AssignmentType>('internal');
    const [selectedExternalUser, setSelectedExternalUser] = useState<ExternalUser | null>(null);
    // E-mail the internal assignee on create (sendAssignmentEmail). On by default.
    const [sendEmail, setSendEmail] = useState(true);
    const [invitedLink, setInvitedLink] = useState<{ link: string; email: string; emailSent: boolean } | null>(null);
    // Set once a work order is created whose delegation failed: what to retry.
    const [failedDelegation, setFailedDelegation] = useState<{ workOrderKey: string; buildingKey: string; message: string } | null>(null);
    const [retrying, setRetrying] = useState(false);

    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    // File attachments state
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [showUploadModal, setShowUploadModal] = useState(false);

    // Location data for maintenance conversion
    const [buildingSummary, setBuildingSummary] = useState<BuildingSummary | null>(null);
    const [component, setComponent] = useState<BaseComponentImpl | null>(null);

    const today = new Date().toISOString().split('T')[0];

    // Fetch building summary and component for maintenance conversion
    useEffect(() => {
        if (!show || !maintenanceItem) {
            setBuildingSummary(null);
            setComponent(null);
            return;
        }

        const fetchLocationData = async () => {
            try {
                const db = getDatabase();

                // Fetch building summary
                const summarySnapshot = await get(ref(db, `buildingsSummary/${maintenanceItem.buildingKey}`));
                if (summarySnapshot.exists()) {
                    setBuildingSummary(summarySnapshot.val() as BuildingSummary);
                }

                // Fetch component if available
                if (maintenanceItem.componentKey) {
                    const componentSnapshot = await get(ref(db, `buildings/${maintenanceItem.buildingKey}/structure/components/${maintenanceItem.componentKey}`));
                    if (componentSnapshot.exists()) {
                        setComponent(new BaseComponentImpl(componentSnapshot.val() as BaseComponent));
                    }
                }
            } catch (err) {
                console.error('Error fetching location data:', err);
            }
        };

        fetchLocationData();
    }, [show, maintenanceItem]);

    // Initialize form based on mode
    useEffect(() => {
        if (!show) return;

        if (issue) {
            // Issue conversion mode: pre-fill from issue
            setValues({
                ...emptyWorkOrderFormValues,
                title: workOrderTitleFromIssue(issueComponentLabel, issue.description),
                description: issue.description,
                buildingKey: issue.buildingKey,
                priority: mapPriority(issue.priority),
                componentKeys: issue.componentKey ? [issue.componentKey] : [],
            });
        } else if (maintenanceItem) {
            // Maintenance conversion mode: pre-fill from maintenance activity
            const template = getTemplateById(maintenanceItem.templateId);
            const activityName = template?.name || maintenanceItem.activityName;
            const componentTypeName = maintenanceItem.componentType
                ? getTranslation(maintenanceItem.componentType)
                : 'Komponent';

            setValues({
                ...emptyWorkOrderFormValues,
                title: `Underhåll: ${activityName}`,
                description: maintenanceWorkOrderDescription(
                    componentTypeName,
                    maintenanceItem.componentLabel,
                    maintenanceItem.comment
                ),
                buildingKey: maintenanceItem.buildingKey,
                priority: WorkOrderPriority.MEDIUM,
                // Pre-fill due date from maintenance next date
                dueDate: maintenanceItem.nextDate,
                componentKeys: maintenanceItem.componentKey ? [maintenanceItem.componentKey] : [],
            });
        } else if (project) {
            // Project creation mode: pre-fill building from project
            setValues({ ...emptyWorkOrderFormValues, buildingKey: project.buildingKey });
        } else if (sourceWorkOrder) {
            // Duplicate mode: pre-fill content fields from the source work order.
            // Assignment, attachments, completion data and audit are intentionally
            // NOT copied — the new order starts unassigned and open.
            setValues({
                ...emptyWorkOrderFormValues,
                title: sourceWorkOrder.title,
                description: sourceWorkOrder.description ?? '',
                buildingKey: sourceWorkOrder.buildingKey,
                priority: sourceWorkOrder.priority,
                // Stored dueDate is `YYYY-MM-DD hh:mm:ss`; the form field is date-only
                // (buildSharedFields re-appends the time on save).
                dueDate: sourceWorkOrder.dueDate ? sourceWorkOrder.dueDate.split(' ')[0] : '',
                componentKeys: sourceWorkOrder.componentKeys ?? [],
                requiresPhoto: sourceWorkOrder.requiresPhoto,
                requiresComment: sourceWorkOrder.requiresComment,
                requiresConditionUpdate: sourceWorkOrder.requiresConditionUpdate,
                enabledSections: workOrderSectionFlagsFrom(sourceWorkOrder.enabledSections),
            });
        } else {
            // Standalone mode: start fresh
            setValues({ ...emptyWorkOrderFormValues });
        }

        // Shell-owned resets
        setAssignedTo('');
        setAssignmentType('internal');
        setSelectedExternalUser(null);
        setPendingFiles([]);
        setShowComponentPicker(false);
        setError(null);
        setFailedDelegation(null);
        setStep('main');
    }, [show, issue, issueComponentLabel, maintenanceItem, project, sourceWorkOrder, getTemplateById, getTranslation, setValues, setError, setShowComponentPicker]);

    // Retry only the delegation of the work order that was already created.
    const retryDelegation = async () => {
        if (!failedDelegation || !selectedExternalUser) return;
        setRetrying(true);
        try {
            const result = await workOrdersApi.assignToExternal(
                failedDelegation.workOrderKey,
                failedDelegation.buildingKey,
                selectedExternalUser.uid,
                undefined,
                linkValidity.payload
            );
            onSuccess?.();
            setFailedDelegation(null);
            setInvitedLink({ link: result.link.url, email: selectedExternalUser.email, emailSent: result.emailSent });
            setStep('created');
        } catch (err) {
            console.error('Delegation retry failed:', err);
            setFailedDelegation({ ...failedDelegation, message: err instanceof Error && err.message ? err.message : 'Kunde inte delegera ärendet' });
        } finally {
            setRetrying(false);
        }
    };

    const handleUserCreated = (user: ExternalUser) => {
        setSelectedExternalUser(user);
        setStep('main');
        // Refresh the parent's user list so getUserName resolves the new
        // external user's name immediately (instead of showing the raw UID)
        onExternalUserCreated?.();
    };

    const handleFiles = useCallback((files: File[]) => {
        const validFiles: File[] = [];
        const errors: string[] = [];

        files.forEach(file => {
            if (file.size > MAX_FILE_SIZE_BYTES) {
                errors.push(`${file.name} är för stor (max ${MAX_FILE_SIZE_MB}MB)`);
            } else {
                validFiles.push(file);
            }
        });

        if (errors.length > 0) {
            setError(errors.join(', '));
        }

        if (validFiles.length > 0) {
            setPendingFiles(prev => [...prev, ...validFiles]);
        }
    }, [setError]);

    const handleRemoveFile = (index: number) => {
        setPendingFiles(prev => prev.filter((_, i) => i !== index));
    };

    // External delegation: the user's link and tasks drive the link block; the
    // validity is pre-filled from the latest due date (+14 days) incl. this one.
    const externalSelected = assignmentType === 'external' && selectedExternalUser ? selectedExternalUser : null;
    const externalLink = useExternalLink(show && externalSelected ? externalSelected.uid : null);
    const linkSuggestion = suggestLinkValidity(externalLink.info?.tasks ?? [], { title: values.title || 'Det här ärendet', dueDate: values.dueDate });
    const linkValidity = useLinkValidityChoice(externalLink.info?.link, externalSelected?.uid ?? null, linkSuggestion);
    const externalLinkKnown = !externalLink.loading && externalLink.info !== null;
    // Nothing is created while the link (and so the validity to send) is unknown or unchosen.
    const externalLinkPending = !!externalSelected && (!externalLinkKnown || !linkValidity.complete);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const validationError = validateShared({ requireBuilding: !prefillsBuilding });
        if (validationError) {
            setError(validationError);
            return;
        }
        if (externalLinkPending) {
            setError('Välj hur länge den externa användarens länk ska gälla.');
            return;
        }

        // For conversions/project mode the building key is pre-filled into the form
        // values, so values.buildingKey is the target in every mode.
        const targetBuildingKey = values.buildingKey;

        setLoading(true);

        try {
            const shared = buildSharedFields();

            // Only use internal assignment if not assigning to external user
            const internalAssignment = assignmentType === 'internal' && assignedTo ? assignedTo : undefined;

            let workOrderKey: string;

            if (isIssueConversion && issue) {
                // Convert issue to work order
                workOrderKey = await issuesApi.convertToWorkOrder(issue.key, {
                    title: shared.title,
                    description: shared.description,
                    priority: shared.priority,
                    dueDate: shared.dueDate,
                    assignedTo: internalAssignment,
                    sendAssignmentEmail: internalAssignment ? sendEmail : undefined,
                    requiresPhoto: shared.requiresPhoto,
                    requiresComment: shared.requiresComment,
                    componentKeys: shared.componentKeys,
                    unitKey: shared.unitKey,
                    unitLabel: shared.unitLabel,
                    requiresConditionUpdate: shared.requiresConditionUpdate,
                    enabledSections: shared.enabledSections,
                });
            } else if (isMaintenanceConversion && maintenanceItem) {
                // Create work order from maintenance activity
                workOrderKey = await workOrdersApi.create({
                    title: shared.title,
                    description: shared.description,
                    buildingKey: maintenanceItem.buildingKey,
                    componentKeys: shared.componentKeys.length > 0 ? shared.componentKeys : undefined,
                    unitKey: shared.unitKey,
                    unitLabel: shared.unitLabel,
                    priority: shared.priority,
                    dueDate: shared.dueDate,
                    assignedTo: internalAssignment,
                    sendAssignmentEmail: internalAssignment ? sendEmail : undefined,
                    requiresPhoto: shared.requiresPhoto,
                    requiresComment: shared.requiresComment,
                    requiresConditionUpdate: shared.requiresConditionUpdate,
                    maintenanceTemplateId: maintenanceItem.templateId,
                    // Pin the activity's component (immutable) so rescheduling on
                    // completion is robust to later edits of componentKeys.
                    maintenanceComponentKey: maintenanceItem.componentKey,
                    enabledSections: shared.enabledSections,
                });
            } else {
                // Create standalone work order (or project-linked work order)
                workOrderKey = await workOrdersApi.create({
                    title: shared.title,
                    description: shared.description,
                    buildingKey: targetBuildingKey,
                    projectKey: project?.key ?? sourceWorkOrder?.projectKey,
                    componentKeys: shared.componentKeys.length > 0 ? shared.componentKeys : undefined,
                    unitKey: shared.unitKey,
                    unitLabel: shared.unitLabel,
                    priority: shared.priority,
                    dueDate: shared.dueDate,
                    assignedTo: internalAssignment,
                    sendAssignmentEmail: internalAssignment ? sendEmail : undefined,
                    requiresPhoto: shared.requiresPhoto,
                    requiresComment: shared.requiresComment,
                    requiresConditionUpdate: shared.requiresConditionUpdate,
                    enabledSections: shared.enabledSections,
                });
            }

            // If external user selected, delegate to them (sends invitation)
            // with the validity chosen in the link block. The work order exists
            // by now: a failure here must not leave the form ready to create it
            // again, so it is kept for a delegation-only retry.
            let guestLink: { url: string; emailSent: boolean } | null = null;
            let delegationError: string | null = null;
            if (assignmentType === 'external' && selectedExternalUser) {
                try {
                    const result = await workOrdersApi.assignToExternal(
                        workOrderKey,
                        targetBuildingKey,
                        selectedExternalUser.uid,
                        undefined,
                        linkValidity.payload
                    );
                    guestLink = { url: result.link.url, emailSent: result.emailSent };
                } catch (delegationErr) {
                    console.error('Work order created, delegation failed:', delegationErr);
                    delegationError = delegationErr instanceof Error && delegationErr.message ? delegationErr.message : 'Kunde inte delegera ärendet';
                }
            }

            // Upload pending attachments
            if (pendingFiles.length > 0) {
                setLoading(false);
                setUploading(true);

                const currentUser = getAuth().currentUser;
                if (!currentUser) {
                    throw new Error('Du måste vara inloggad');
                }

                const uploadErrors: string[] = [];
                for (const file of pendingFiles) {
                    const result = await uploadWorkOrderAttachment(
                        workOrderKey,
                        file,
                        currentUser.uid
                    );
                    if (!result.success) {
                        uploadErrors.push(`${file.name}: ${result.error}`);
                    }
                }

                if (uploadErrors.length > 0) {
                    // Work order was created, but some uploads failed
                    console.error('Some attachments failed to upload:', uploadErrors);
                    if (delegationError) {
                        // The delegation-failed step below takes over; say both.
                        delegationError = `${delegationError} Vissa bilagor kunde inte heller laddas upp: ${uploadErrors.join(', ')}`;
                    } else {
                        setError(`Ärende skapat, men vissa bilagor kunde inte laddas upp: ${uploadErrors.join(', ')}`);
                        // Don't return - let the modal close after a brief delay to show the message
                        setTimeout(() => {
                            onSuccess?.();
                            onHide();
                        }, 2000);
                        return;
                    }
                }
            }

            onSuccess?.();
            if (delegationError) {
                setFailedDelegation({ workOrderKey, buildingKey: targetBuildingKey, message: delegationError });
                setStep('delegation-failed');
                return;
            }
            if (guestLink && selectedExternalUser) {
                // Keep the modal open on the link step; closing it is the user's call.
                setInvitedLink({ link: guestLink.url, email: selectedExternalUser.email, emailSent: guestLink.emailSent });
                setStep('created');
                return;
            }
            onHide();
        } catch (err: any) {
            console.error('Error creating work order:', err);
            setError(err.message || 'Kunde inte skapa ärende');
        } finally {
            setLoading(false);
            setUploading(false);
        }
    };

    // Get list of users for assignment dropdown. Exclude external users: they must be
    // delegated via the guest-token flow (externalUserId), not assigned internally.
    // accessToWeb is intentionally not filtered - iOS-only internal users are still
    // valid assignees.
    const userList = Object.values(users || {})
        .filter(u => !u.deleted && !u.isExternal)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv'));

    // Get sorted buildings list
    const buildingsList = (buildings || [])
        .slice()
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'sv'));

    return (
        <>
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {step === 'create-user'
                        ? 'Lägg till extern användare'
                        : step === 'created'
                            ? 'Inbjudan skickad'
                        : step === 'delegation-failed'
                            ? 'Ärendet är skapat'
                        : isIssueConversion
                            ? 'Skapa ärende från felanmälan'
                            : isMaintenanceConversion
                                ? 'Skapa ärende från underhåll'
                                : isDuplicate
                                    ? 'Duplicera ärende'
                                    : project
                                        ? 'Skapa ärende/delmoment'
                                        : 'Skapa ärende'}
                </Modal.Title>
            </Modal.Header>

            {step === 'created' && invitedLink ? (
                <>
                    <Modal.Body>
                        <Alert variant={invitedLink.emailSent ? 'success' : 'warning'} className="mb-3">
                            {invitedLink.emailSent
                                ? <>Ärendet är skapat och en inbjudan har skickats till <strong>{invitedLink.email}</strong>.</>
                                : <>Ärendet är skapat. {WORK_ORDER_LABELS.externalEmailNotSent}</>}
                        </Alert>
                        <GuestLinkField link={invitedLink.link} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="primary" onClick={onHide}>Stäng</Button>
                    </Modal.Footer>
                </>
            ) : step === 'delegation-failed' && failedDelegation ? (
                <>
                    <Modal.Body>
                        <Alert variant="warning" className="mb-0">
                            Ärendet är skapat, men delegeringen till {selectedExternalUser?.name ?? 'den externa användaren'} misslyckades: {failedDelegation.message}
                            <div className="mt-2 small">Försök igen, eller stäng och delegera från ärendet.</div>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHide} disabled={retrying}>Stäng</Button>
                        <Button variant="primary" onClick={() => { void retryDelegation(); }} disabled={retrying || !selectedExternalUser}>
                            {retrying ? 'Delegerar...' : 'Försök delegera igen'}
                        </Button>
                    </Modal.Footer>
                </>
            ) : step === 'create-user' ? (
                <CreateExternalUserForm
                    onUserCreated={handleUserCreated}
                    onCancel={() => setStep('main')}
                />
            ) : (
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Issue info (read-only, only in issue conversion mode) */}
                    {isIssueConversion && issue && (
                        <div className="bg-light p-3 rounded mb-3">
                            <h6 className="mb-2">Felanmälan</h6>
                            <p className="mb-1"><strong>Byggnad:</strong> {issue.buildingName}</p>
                            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}><strong>Beskrivning:</strong> {issue.description}</p>
                            {issue.componentCategory && !issue.componentKey && (
                                <p className="mb-0 mt-1 text-muted">
                                    <strong>Kategori:</strong> {issueComponentCategoryText(issue.componentCategory)}
                                    {' '}(ingen komponent kopplad – välj en nedan om den finns)
                                </p>
                            )}
                            {issue.photos && Object.keys(issue.photos).length > 0 && (
                                <p className="mb-0 mt-1">
                                    <strong>Bilder:</strong> {Object.keys(issue.photos).length} st (kopieras till ärendet)
                                </p>
                            )}
                        </div>
                    )}

                    {/* Maintenance info (read-only, only in maintenance conversion mode) */}
                    {isMaintenanceConversion && maintenanceItem && (() => {
                        const template = getTemplateById(maintenanceItem.templateId);
                        const activityName = template?.name || maintenanceItem.activityName;
                        const componentTypeName = maintenanceItem.componentType
                            ? getTranslation(maintenanceItem.componentType)
                            : 'Okänd komponent';
                        const componentUrl = `/building/${maintenanceItem.buildingKey}/component/${maintenanceItem.componentKey}`;

                        return (
                            <div className="bg-light p-3 rounded mb-3">
                                <h6 className="mb-2">Underhållsaktivitet</h6>
                                <p className="mb-1"><strong>Byggnad:</strong> {maintenanceItem.buildingName}</p>
                                <p className="mb-1">
                                    <strong>Komponent:</strong>{' '}
                                    <a
                                        href={componentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-none"
                                    >
                                        {componentTypeName}
                                        {maintenanceItem.componentLabel && (
                                            <span className="text-muted ms-1">({maintenanceItem.componentLabel})</span>
                                        )}
                                    </a>
                                </p>
                                <p className="mb-1"><strong>Aktivitet:</strong> {activityName}</p>
                                <p className="mb-0"><strong>Planerat datum:</strong> {maintenanceItem.nextDate}</p>
                                {maintenanceItem.cost !== undefined && maintenanceItem.cost > 0 && (
                                    <p className="mb-0 mt-1">
                                        <strong>Beräknad kostnad:</strong> {maintenanceItem.cost.toLocaleString('sv-SE')} kr
                                    </p>
                                )}
                                {/* Location info */}
                                {buildingSummary && (
                                    <div className="mt-3 pt-3 border-top">
                                        <LocationSection
                                            buildingKey={maintenanceItem.buildingKey}
                                            buildingSummary={buildingSummary}
                                            component={component}
                                            showVisualMarkers={true}
                                            compact={true}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Building selection (only when building not prefilled) */}
                    {!prefillsBuilding && (
                        <Form.Group className="mb-3">
                            <Form.Label>Byggnad *</Form.Label>
                            <Form.Select
                                value={values.buildingKey}
                                onChange={(e) => {
                                    // Components belong to a building; reset on change.
                                    setField('buildingKey', e.target.value);
                                    setField('componentKeys', []);
                                }}
                                required
                            >
                                <option value="">Välj byggnad...</option>
                                {buildingsList.map(building => (
                                    <option key={building.key} value={building.key}>
                                        {building.name || 'Namnlös byggnad'}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {/* Title */}
                    <Form.Group className="mb-3">
                        <Form.Label>Titel *</Form.Label>
                        <Form.Control
                            type="text"
                            value={values.title}
                            onChange={(e) => setField('title', e.target.value)}
                            placeholder="Beskriv arbetsuppgiften"
                            maxLength={200}
                            required
                        />
                        <small className={`d-block mt-1 text-end ${values.title.length >= 200 ? 'text-danger' : 'text-muted'}`}>
                            {values.title.length} / 200
                        </small>
                    </Form.Group>

                    {/* Description */}
                    <Form.Group className="mb-3">
                        <Form.Label>Beskrivning</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={values.description}
                            onChange={(e) => setField('description', e.target.value)}
                            placeholder="Detaljerad beskrivning av arbetet som ska utföras"
                            maxLength={2000}
                        />
                        <small className={`d-block mt-1 text-end ${values.description.length >= 2000 ? 'text-danger' : 'text-muted'}`}>
                            {values.description.length} / 2000
                        </small>
                    </Form.Group>

                    {/* File Attachments */}
                    <Form.Group className="mb-3">
                        <Form.Label>Bilagor</Form.Label>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowUploadModal(true)}
                                disabled={loading || uploading}
                            >
                                <Paperclip size={16} className="me-1" />
                                Ladda upp bilagor
                            </Button>
                            <Form.Text className="text-muted mb-0">
                                Max {MAX_FILE_SIZE_MB}MB per fil
                            </Form.Text>
                        </div>
                        {pendingFiles.length > 0 && (
                            <ul className="list-group list-group-flush">
                                {pendingFiles.map((file, index) => (
                                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center py-2 px-0">
                                        <span className="text-truncate me-2" style={{ maxWidth: '300px' }}>
                                            {file.name}
                                        </span>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger p-0"
                                            onClick={() => handleRemoveFile(index)}
                                            disabled={loading || uploading}
                                        >
                                            <X size={18} />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Form.Group>

                    {/* Linked components */}
                    <WorkOrderComponentLinker
                        componentKeys={values.componentKeys}
                        componentDisplayName={componentDisplayName}
                        onRemove={removeComponentKey}
                        onOpenPicker={() => setShowComponentPicker(true)}
                        canPick={!!values.buildingKey}
                        loading={loadingFullBuilding}
                        ready={!!fullBuilding}
                    />

                    {unitOptions.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label>Lägenhet / lokal</Form.Label>
                            <Form.Select value={values.unitKey} onChange={e => setField('unitKey', e.target.value)}>
                                <option value="">Ingen</option>
                                {unitOptions.map(unit => (
                                    <option key={unit.key} value={unit.key}>{unitDisplayName(unit)}</option>
                                ))}
                            </Form.Select>
                            <Form.Text muted>Ärendet visas på enheten och går att söka på enhetens namn.</Form.Text>
                        </Form.Group>
                    )}

                    <div className="row">
                        {/* Priority */}
                        <Form.Group className="mb-3 col-md-6">
                            <Form.Label>Prioritet</Form.Label>
                            <Form.Select
                                value={values.priority}
                                onChange={(e) => setField('priority', e.target.value as WorkOrderPriority)}
                            >
                                {Object.values(WorkOrderPriority).map(p => (
                                    <option key={p} value={p}>
                                        {translateWorkOrderPriority(p)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {/* Due date */}
                        <Form.Group className="mb-3 col-md-6">
                            <Form.Label>Förfallodatum</Form.Label>
                            <Form.Control
                                type="date"
                                value={values.dueDate}
                                onChange={(e) => setField('dueDate', e.target.value)}
                                min={today}
                                max="9999-12-31"
                            />
                        </Form.Group>
                    </div>

                    {/* Assignment: the same Interna/Externa tabs as the Tilldela modal */}
                    <Form.Group className="mb-3">
                        <AssignmentPicker
                            activeTab={assignmentType}
                            onTabChange={setAssignmentType}
                            internalCount={assignmentType === 'internal' && assignedTo ? 1 : 0}
                            externalCount={assignmentType === 'external' && selectedExternalUser ? 1 : 0}
                            internalUsers={userList}
                            selectedInternalUser={assignedTo}
                            onInternalUserChange={setAssignedTo}
                            internalUnassignedLabel="Ingen tilldelning (öppen för alla)"
                            sendEmail={sendEmail}
                            onSendEmailChange={setSendEmail}
                            selectedExternalUser={selectedExternalUser}
                            onExternalUserChange={setSelectedExternalUser}
                            onCreateExternalUser={() => setStep('create-user')}
                            externalExtra={selectedExternalUser && (
                                <>
                                    <Form.Text className="text-muted d-block mb-2">
                                        En e-post med länk skickas till {selectedExternalUser.email}. Gästlänken visas när ärendet har skapats.
                                    </Form.Text>
                                    <PersonalLinkBlock
                                        recipientName={selectedExternalUser.name}
                                        taskType="workOrder"
                                        info={externalLinkKnown ? externalLink.info : null}
                                        choice={linkValidity}
                                        loading={externalLink.loading}
                                        error={externalLinkKnown ? null : externalLink.error}
                                        onRetry={() => { externalLink.clearError(); void externalLink.refetch(); }}
                                        disabled={loading || uploading}
                                        idSuffix="create-workorder"
                                    />
                                </>
                            )}
                            disabled={loading || uploading}
                            idSuffix="create-workorder"
                        />
                    </Form.Group>

                    {/* Requirements + sections (two columns) */}
                    <Row className="mt-4">
                        <Col md={6}>
                            <WorkOrderRequirementsCheckboxes
                                requiresPhoto={values.requiresPhoto}
                                requiresComment={values.requiresComment}
                                requiresConditionUpdate={values.requiresConditionUpdate}
                                onRequiresPhotoChange={(v) => setField('requiresPhoto', v)}
                                onRequiresCommentChange={(v) => setField('requiresComment', v)}
                                onRequiresConditionUpdateChange={(v) => setField('requiresConditionUpdate', v)}
                                hasLinkedComponents={values.componentKeys.length > 0}
                                idPrefix="create-"
                            />
                        </Col>
                        <Col md={6}>
                            <WorkOrderSectionsCheckboxes
                                value={values.enabledSections}
                                onChange={(next) => setField('enabledSections', next)}
                                idPrefix="create-"
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={loading || uploading}>
                        Avbryt
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading || uploading || externalLinkPending}>
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Skapar...
                            </>
                        ) : uploading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Laddar upp bilagor...
                            </>
                        ) : (
                            'Skapa ärende'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
            )}
        </Modal>

        {/* Component picker (multi-select) */}
        {fullBuilding && (
            <ComponentPickerModal
                show={showComponentPicker}
                onHide={() => setShowComponentPicker(false)}
                building={fullBuilding}
                multiSelect
                selectedKeys={values.componentKeys}
                onSelectMultiple={(keys) => {
                    setField('componentKeys', keys);
                    setShowComponentPicker(false);
                }}
                onComponentPatched={patchComponent}
            />
        )}

        {/* Upload picker (drag-drop, paste, or pick from device) */}
        <UploadPickerModal
            show={showUploadModal}
            onHide={() => setShowUploadModal(false)}
            onFilesSelected={handleFiles}
            multiple
            title="Ladda upp"
        />
        </>
    );
};

export default CreateWorkOrderModal;
